import React, { lazy, Suspense, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router";
import { Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import ErrorBoundary from "../components/common/ErrorBoundary";
import Sidebar from "../components/common/Sidebar";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb";
import { SidebarProvider, SidebarInset, SidebarTrigger } from "@/components/ui/sidebar";
import { Separator } from "@/components/ui/separator";
import { useAuth } from "../contexts/AuthContext";
import CasesPage from "./CasesPage";
import ClientConfigurationPage from "./ClientConfigurationPage";
import ClientConfigurationsPage from "./ClientConfigurationsPage";
import ClientsPage from "./ClientsPage";
import OrganizationPage from "./OrganizationPage";
import OrganizationsPage from "./OrganizationsPage";
import { useOrganization } from "../contexts/OrganizationContext";
import UsersPage from "./UsersPage";
import OrganizationConfigurationPage from "./OrganizationConfigurationPage";
import IntakesPage from "./IntakesPage";
import AuditLogsPage from "./AuditLogsPage";
import ExecuteSqlScriptsPage from "./ExecuteSqlScriptsPage"
import RecalculateFilePrioritiesPage from "./RecalculateFilePrioritiesPage";
import BulkDeletePage from "./BulkDeletePage";
import ResolveConflictsByRulePage from "./ResolveConflictsByRulePage";
import JobsPage from "./JobsPage";
import FirmDocumentsFoldersPage from "./FirmDocumentsFoldersPage";
import FirmDocumentsFolderPage from "./FirmDocumentsFolderPage";
import ProvidersPage from "./ProvidersPage";
import ProviderPage from "./ProviderPage";
import TemplatesPage from "./TemplatesPage";
import { UserRole } from "../models/User";
import DiscoveryCyclesPage from "./DiscvoeryCyclesPage";
import { AuthorizationResult } from "../models/AuthorizationResult";
import OrganizationsRepository from "../repositories/OrganizationsRepository";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import axios from "axios";
import TemplateFilesPage from "./TemplateFilesPage";
import { ProviderFilesPage } from "./ProviderFilesPage";
import { QUERY_KEYS } from "../constants/QueryKeys";
import { IntakePage } from "./IntakePage";
import { CasePage } from "./CasePage";
import { NAVIGATION_KEYS } from "../constants/NavigationKeys";
import { CaseFilesPage } from "./CaseFilesPage";
import { IntakeFilesPage } from "./IntakeFilesPage";
import { FirmDocumentsPage } from "./FirmDocumentsPage";
import { TemplatePage } from "./TemplatePage";
import VersionsPage from "./VersionsPage";
import FlaggedPathsPage from "./FlaggedPathsPage";
import RenamedFoldersPage from "./RenamedFoldersPage";
import SourceLinksPage from "./SourceLinksPage";
import DetailBreadcrumbContext from "../contexts/DetailBreadcrumbContext";
import { useDocsManifest } from "../hooks/useDocs";
import WhatsNewDialog from "../components/Docs/WhatsNewDialog";

// Loaded on demand: the markdown renderer only matters once someone opens the docs.
const DocsPage = lazy(() => import("./DocsPage"));

const DETAIL_PAGE_PARENTS: Record<string, { label: string; path: string }> = {
  [NAVIGATION_KEYS.CASE]: { label: "Cases", path: "cases" },
  [NAVIGATION_KEYS.INTAKE]: { label: "Intakes", path: "intakes" },
  [NAVIGATION_KEYS.PROVIDER]: { label: "Providers", path: "providers" },
  [NAVIGATION_KEYS.TEMPLATE]: { label: "Templates", path: "templates" },
  [NAVIGATION_KEYS.FIRM_DOCUMENTS_FOLDER]: { label: "Firm Documents Folders", path: "firm-documents-folders" },
  [NAVIGATION_KEYS.CLIENT_CONFIGURATION]: { label: "Client Configurations", path: "client-configurations" },
};

interface DashboardPageProps {
  selectedNavigation: string;
}

export interface AccessData {
  hasAccess: boolean;
  isTemporary: boolean;
  endDate: Date | null;
  isAssignedUser?: boolean;
}

const DashboardPage: React.FC<DashboardPageProps> = ({
  selectedNavigation: selectedItem,
}) => {
  const { organizationId, configurationId } = useParams<{ organizationId?: string; configurationId?: string; }>();
  const navigate = useNavigate();
  const organizationsRepository = new OrganizationsRepository();
  const { setSelectedOrganizationId } = useOrganization();
  const queryClient = useQueryClient();
  const { getDecodedToken, login, isAuthenticated, refreshToken, hasManagedAccessToOrganization } = useAuth();
  const decodedToken = getDecodedToken();
  const userRole = decodedToken?.roles?.toString().toLowerCase() as UserRole;
  const userName = decodedToken?.username;
  const userId = decodedToken?.id;
  const previousOrgIdRef = useRef<string | undefined>(organizationId);
  const [isAssignedUser, setIsAssignedUser] = React.useState<boolean>(false);
  const [currentOrganizationIntactId, setCurrentOrganizationIntactId] = React.useState<string | undefined>(undefined);
  const [currentOrganizationName, setCurrentOrganizationName] = React.useState<string | undefined>(undefined);
  const [currentOrganizationAssignedUserId, setCurrentOrganizationAssignedUserId] = React.useState<string | undefined>(undefined);
  const [entityLabel, setEntityLabel] = useState<string | null>(null);
  const setEntityLabelStable = useCallback((label: string | null) => setEntityLabel(label), []);

  const accessData = useMemo<AccessData | null>(() => {
    if (!organizationId)
      return null;

    if (!decodedToken)
      return null;

    const now = new Date();

    if (userRole === UserRole.Admin) {
      return {
        hasAccess: true,
        isTemporary: false,
        endDate: null
      };
    }

    const managedOrganizations = decodedToken?.managed_organizations;
    if (managedOrganizations?.split(",").includes(organizationId)) {
      return {
        hasAccess: true,
        isTemporary: false,
        endDate: null,
        isAssignedUser: true
      };
    }

    const temporaryAccessOrganizations: string | undefined = decodedToken?.temporary_access_organizations;
    const accessList: { orgId: string; endDate: Date }[] = temporaryAccessOrganizations
      ? temporaryAccessOrganizations
        .split(",")
        .map((entry) => {
          const [orgId, endDateStr] = entry.split(";");
          return {
            orgId,
            endDate: new Date(endDateStr),
          };
        })
      : [];

    const foundAccess = accessList.find(
      (access) => access.orgId === organizationId && access.endDate > now
    );

    return {
      hasAccess: !!foundAccess,
      isTemporary: !!foundAccess,
      endDate: foundAccess?.endDate || null,
      // Temp-access editors are peers of assigned users for write-gating;
      // the Sidebar distinguishes the two via accessData.isTemporary.
      isAssignedUser: !!foundAccess
    };
  }, [organizationId, decodedToken, userRole]);

  const organizationNavigationInfoQuery = useQuery({ queryKey: [QUERY_KEYS.ORGANIZATION_NAVIGATION_INFO, organizationId], queryFn:
    async () => await organizationsRepository.getOrganizationNavigationInfo(organizationId!), staleTime: Infinity,
      gcTime: 1000 * 60 * 60 * 24,
      enabled: !!organizationId && isAuthenticated,
      refetchOnWindowFocus: false,
      refetchOnMount: false,
      refetchOnReconnect: false, });

  useEffect(() => {
    if (accessData?.isAssignedUser !== undefined) {
      setIsAssignedUser(accessData.isAssignedUser);
    }
  }, [accessData]);

  const hasDocumentAccess = accessData?.hasAccess ?? false;
  const { data: docsManifest } = useDocsManifest();
  const unseenReleases = docsManifest?.unseenReleases ?? 0;
  const docsBasePath = organizationId ? `/organization/${organizationId}/docs` : "/docs";

  useEffect(() => {
    if (previousOrgIdRef.current !== organizationId && previousOrgIdRef.current !== undefined) {
      queryClient.removeQueries({
        predicate: (query) => {
          const key = query.queryKey;
          return Array.isArray(key) && key.length > 1 && key[1] === previousOrgIdRef.current;
        },
      });
    }
    previousOrgIdRef.current = organizationId;
  }, [organizationId, queryClient]);

  useEffect(() => {
    if (!isAuthenticated) {
      navigate("/login");
      return;
    }

    if (organizationId !== undefined) {
      setSelectedOrganizationId(organizationId);
    } else {
      setSelectedOrganizationId(null);
    }

    const requiresDocumentAccess: string[] = [
      NAVIGATION_KEYS.CASES,
      NAVIGATION_KEYS.CASE,
      NAVIGATION_KEYS.CASE_FILES,
      NAVIGATION_KEYS.INTAKES,
      NAVIGATION_KEYS.INTAKE,
      NAVIGATION_KEYS.INTAKE_FILES,
      NAVIGATION_KEYS.PROVIDERS,
      NAVIGATION_KEYS.PROVIDER,
      NAVIGATION_KEYS.PROVIDER_FILES,
      NAVIGATION_KEYS.TEMPLATES,
      NAVIGATION_KEYS.TEMPLATE,
      NAVIGATION_KEYS.TEMPLATE_FILES,
      NAVIGATION_KEYS.FIRM_DOCUMENTS_FOLDERS,
      NAVIGATION_KEYS.FIRM_DOCUMENTS_FOLDER,
      NAVIGATION_KEYS.FIRM_DOCUMENTS,
      NAVIGATION_KEYS.FLAGGED_PATHS,
      NAVIGATION_KEYS.RENAMED_FOLDERS,
      NAVIGATION_KEYS.SOURCE_LINKS,
      NAVIGATION_KEYS.EXECUTE_SQL_SCRIPTS,
      NAVIGATION_KEYS.RECALCULATE_FILE_PRIORITIES,
      NAVIGATION_KEYS.BULK_DELETE,
      NAVIGATION_KEYS.RESOLVE_CONFLICTS_BY_RULE
    ];
    const requiresDocumentAccessForCurrentPage = requiresDocumentAccess.includes(selectedItem);
    if (requiresDocumentAccessForCurrentPage && !hasDocumentAccess && organizationId) {
      navigate("/organization/" + organizationId);
      return;
    }

    const requiresAdminRole: string[] = [
      NAVIGATION_KEYS.USERS
    ];
    const requiresAdminRoleForCurrentPage = requiresAdminRole.includes(selectedItem);
    if (requiresAdminRoleForCurrentPage && userRole !== UserRole.Admin) {
      navigate("/organizations/");
      return;
    }

    const requiresAdminOrEditorRole: string[] = [
      NAVIGATION_KEYS.JOBS,
      NAVIGATION_KEYS.VERSIONS,
      NAVIGATION_KEYS.EXECUTE_SQL_SCRIPTS,
      NAVIGATION_KEYS.RECALCULATE_FILE_PRIORITIES,
      NAVIGATION_KEYS.BULK_DELETE,
      NAVIGATION_KEYS.RESOLVE_CONFLICTS_BY_RULE
    ];
    const requiresAdminOrEditorRoleForCurrentPage = requiresAdminOrEditorRole.includes(selectedItem);
    if (requiresAdminOrEditorRoleForCurrentPage && (userRole !== UserRole.Admin && userRole !== UserRole.Editor)) {
      navigate("/organizations/");
      return;
    }

  }, [
    isAuthenticated,
    navigate,
    organizationId,
    setSelectedOrganizationId,
    selectedItem,
    hasDocumentAccess,
    userRole
  ]);

  useEffect(() => {
    if (organizationNavigationInfoQuery.data) {
      setCurrentOrganizationIntactId(organizationNavigationInfoQuery.data.intactId);
      setCurrentOrganizationName(organizationNavigationInfoQuery.data.name);
      setCurrentOrganizationAssignedUserId(organizationNavigationInfoQuery.data.assignedUserId);
    }
  }, [organizationNavigationInfoQuery.data, organizationId]);

  useEffect(() => {
    const refreshTokenIfNeeded = async () => {
      // Compare the JWT's managed_organizations claim (real assignment) against
      // the backend's current assignedUserId. isAssignedUser is no longer the
      // authoritative source — it also reflects temp access.
      const isManagedOwner = organizationId ? hasManagedAccessToOrganization(organizationId) : false;
      const assignmentChanged = (isManagedOwner && currentOrganizationAssignedUserId !== userId)
        || (!isManagedOwner && currentOrganizationAssignedUserId === userId);
      const shouldRefreshToken = userRole === UserRole.Editor && assignmentChanged;

      if (shouldRefreshToken) {
        try {
          const apiUrl = import.meta.env.VITE_API_URL ?? "/";
          const api = axios.create({ baseURL: apiUrl, timeout: 30000 });
          const response = await api.post(`api/RefreshToken`, { refreshToken: refreshToken });
          const tokens = response.data as AuthorizationResult;
          login(tokens.accessToken, tokens.refreshToken);
        } catch (error) {
          alert("Session expired!");
          navigate("/login");
          console.error("Failed to refresh access token:", error);
        }
      }
    };
    refreshTokenIfNeeded();
  }, [organizationId, currentOrganizationAssignedUserId, userId]);

  useEffect(() => {
    const pageName = entityLabel || selectedItem || "Dashboard";
    const orgLabel = currentOrganizationIntactId && currentOrganizationName
      ? `${currentOrganizationIntactId} – ${currentOrganizationName}`
      : currentOrganizationIntactId || currentOrganizationName;
    document.title = orgLabel
      ? `${pageName} — ${orgLabel} — Neos Files Sync`
      : `${pageName} — Neos Files Sync`;
    return () => { document.title = "Neos Files Sync"; };
  }, [selectedItem, entityLabel, currentOrganizationName, currentOrganizationIntactId]);

  const detailParent = DETAIL_PAGE_PARENTS[selectedItem];

  const breadcrumbCrumbs = useMemo(() => {
    if (!organizationId) return null;
    const orgLabel = currentOrganizationIntactId && currentOrganizationName
      ? `${currentOrganizationIntactId} – ${currentOrganizationName}`
      : currentOrganizationIntactId || currentOrganizationName || "";
    const pageName = selectedItem?.replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase());
    return { orgLabel, pageName, isOverview: selectedItem === NAVIGATION_KEYS.OVERVIEW };
  }, [organizationId, currentOrganizationName, currentOrganizationIntactId, selectedItem]);

  if (!isAuthenticated)
    return <></>;

  let contentComponent;
  switch (selectedItem) {
    case NAVIGATION_KEYS.ORGANIZATIONS:
      contentComponent = (
        <OrganizationsPage
          userRole={userRole}
          userId={userId ?? ""}
        />
      );
      break;
    case NAVIGATION_KEYS.OVERVIEW:
      contentComponent = (
        <OrganizationPage
          organizationId={organizationId ?? ""}
          userRole={userRole}
          isAssignedUser={isAssignedUser}
          hasDocumentAccess={hasDocumentAccess}
        />
      );
      break;
    case NAVIGATION_KEYS.DISCOVERY_CYCLES:
      contentComponent = <DiscoveryCyclesPage />;
      break;
    case NAVIGATION_KEYS.AUDIT_LOGS:
      contentComponent = (
        <AuditLogsPage
          canUseConflictRules={(userRole === UserRole.Admin || userRole === UserRole.Editor) && hasDocumentAccess}
        />
      );
      break;
    case NAVIGATION_KEYS.ORGANIZATION_CONFIGURATION:
      contentComponent = (
        <OrganizationConfigurationPage
          organizationId={organizationId ?? ""}
          userRole={userRole}
          isAssignedUser={isAssignedUser}
        />
      );
      break;
    case NAVIGATION_KEYS.CLIENT_CONFIGURATIONS:
      contentComponent = (
        <ClientConfigurationsPage
          organizationId={organizationId ?? ""}
          userRole={userRole}
          isAssignedUser={isAssignedUser}
        />
      );
      break;
    case NAVIGATION_KEYS.CLIENT_CONFIGURATION:
      contentComponent = (
        <ClientConfigurationPage
          configurationId={configurationId ?? ""}
          organizationId={organizationId ?? ""}
          userRole={userRole}
          isAssignedUser={isAssignedUser}
        />
      );
      break;
    case NAVIGATION_KEYS.CLIENTS:
      contentComponent = (
        <ClientsPage
          userRole={userRole}
          isAssignedUser={isAssignedUser}
        />
      );
      break;
    case NAVIGATION_KEYS.CASES:
      contentComponent = (
        <CasesPage
          userRole={userRole}
          isAssignedUser={isAssignedUser}
        />
      );
      break;
    case NAVIGATION_KEYS.CASE:
      contentComponent = (
        <CasePage
          userRole={userRole}
          isAssignedUser={isAssignedUser}
        />
      );
      break;
    case NAVIGATION_KEYS.CASE_FILES:
      contentComponent = (
        <CaseFilesPage
          userRole={userRole}
          isAssignedUser={isAssignedUser}
        />
      );
      break;
    case NAVIGATION_KEYS.INTAKES:
      contentComponent = (
        <IntakesPage
          userRole={userRole}
          isAssignedUser={isAssignedUser}
        />
      );
      break;
    case NAVIGATION_KEYS.INTAKE:
      contentComponent = (
        <IntakePage
          userRole={userRole}
          isAssignedUser={isAssignedUser}
        />
      );
      break;
    case NAVIGATION_KEYS.INTAKE_FILES:
      contentComponent = (
        <IntakeFilesPage
          userRole={userRole}
          isAssignedUser={isAssignedUser}
        />
      );
      break;
    case NAVIGATION_KEYS.FIRM_DOCUMENTS_FOLDERS:
      contentComponent = (
        <FirmDocumentsFoldersPage
          userRole={userRole}
          isAssignedUser={isAssignedUser}
        />
      );
      break;
    case NAVIGATION_KEYS.FIRM_DOCUMENTS_FOLDER:
      contentComponent = (
        <FirmDocumentsFolderPage
          userRole={userRole}
          isAssignedUser={isAssignedUser}
        />
      );
      break;
    case NAVIGATION_KEYS.FIRM_DOCUMENTS:
      contentComponent = (
        <FirmDocumentsPage
          userRole={userRole}
          isAssignedUser={isAssignedUser}
        />
      );
      break;
    case NAVIGATION_KEYS.PROVIDERS:
      contentComponent = (
        <ProvidersPage
          userRole={userRole}
          isAssignedUser={isAssignedUser}
        />
      );
      break;
    case NAVIGATION_KEYS.PROVIDER:
      contentComponent = (
        <ProviderPage
          userRole={userRole}
          isAssignedUser={isAssignedUser}
        />
      );
      break;
    case NAVIGATION_KEYS.PROVIDER_FILES:
      contentComponent = (
        <ProviderFilesPage
          userRole={userRole}
          isAssignedUser={isAssignedUser}
        />
      );
      break;
    case NAVIGATION_KEYS.TEMPLATES:
      contentComponent = (
        <TemplatesPage
          userRole={userRole}
          isAssignedUser={isAssignedUser}
        />
      );
      break;
    case NAVIGATION_KEYS.TEMPLATE:
      contentComponent = (
        <TemplatePage
          userRole={userRole}
          isAssignedUser={isAssignedUser}
        />
      );
      break;
    case NAVIGATION_KEYS.TEMPLATE_FILES:
      contentComponent = (
        <TemplateFilesPage
          userRole={userRole}
          isAssignedUser={isAssignedUser}
        />
      );
      break;
    case NAVIGATION_KEYS.FLAGGED_PATHS:
      contentComponent = (
        <FlaggedPathsPage
          userRole={userRole}
          isAssignedUser={isAssignedUser}
        />
      );
      break;
    case NAVIGATION_KEYS.RENAMED_FOLDERS:
      contentComponent = (
        <RenamedFoldersPage
          userRole={userRole}
          isAssignedUser={isAssignedUser}
        />
      );
      break;
    case NAVIGATION_KEYS.SOURCE_LINKS:
      contentComponent = (
        <SourceLinksPage
          userRole={userRole}
          isAssignedUser={isAssignedUser}
        />
      );
      break;
    case NAVIGATION_KEYS.USERS:
      contentComponent = <UsersPage />;
      break;
    case NAVIGATION_KEYS.VERSIONS:
      contentComponent = <VersionsPage />;
      break;
    case NAVIGATION_KEYS.JOBS:
      contentComponent = <JobsPage />;
      break;
    case NAVIGATION_KEYS.DOCS:
      contentComponent = (
        <Suspense fallback={<Skeleton className="h-64 w-full" />}>
          <DocsPage />
        </Suspense>
      );
      break;
    case NAVIGATION_KEYS.EXECUTE_SQL_SCRIPTS:
      contentComponent = (
        <ExecuteSqlScriptsPage
          userRole={userRole}
          organizationName={currentOrganizationName ?? ""}
          organizationId={organizationId ?? ""}
        />
      );
      break;
    case NAVIGATION_KEYS.RECALCULATE_FILE_PRIORITIES:
      contentComponent = (
        <RecalculateFilePrioritiesPage
          userRole={userRole}
          organizationName={currentOrganizationName ?? ""}
          organizationId={organizationId ?? ""}
        />
      );
      break;
    case NAVIGATION_KEYS.BULK_DELETE:
      contentComponent = (
        <BulkDeletePage
          userRole={userRole}
          organizationName={currentOrganizationName ?? ""}
          organizationId={organizationId ?? ""}
        />
      );
      break;
    case NAVIGATION_KEYS.RESOLVE_CONFLICTS_BY_RULE:
      // Keyed by organization: a preview counts one organization's files, so it must not survive a switch to another.
      contentComponent = (
        <ResolveConflictsByRulePage
          key={organizationId}
          userRole={userRole}
          organizationName={currentOrganizationName ?? ""}
          organizationId={organizationId ?? ""}
        />
      );
      break;
    default:
      contentComponent = null;
  }

  return (
    <SidebarProvider defaultOpen={true}>
      <Sidebar
        selectedItem={selectedItem}
        accessData={accessData}
        userRole={userRole}
        userName={userName ?? ""}
        isAssignedUser={isAssignedUser}
        isLoading={organizationNavigationInfoQuery.isLoading}
        currentOrganizationIntactId={currentOrganizationIntactId}
        currentOrganizationName={currentOrganizationName}
        unseenReleases={unseenReleases}
      />
      <SidebarInset className="overflow-hidden">
        <header className="flex h-10 shrink-0 items-center gap-2 border-b px-3">
          <SidebarTrigger />
          <Separator orientation="vertical" className="h-4" />
          {breadcrumbCrumbs && (
            <Breadcrumb>
              <BreadcrumbList>
                <BreadcrumbItem>
                  <BreadcrumbLink className="cursor-pointer" onClick={() => navigate("/organizations")}>
                    Organizations
                  </BreadcrumbLink>
                </BreadcrumbItem>
                <BreadcrumbSeparator />
                {breadcrumbCrumbs.isOverview ? (
                  <BreadcrumbItem>
                    <BreadcrumbPage>{breadcrumbCrumbs.orgLabel}</BreadcrumbPage>
                  </BreadcrumbItem>
                ) : (
                  <>
                    <BreadcrumbItem>
                      <BreadcrumbLink className="cursor-pointer" onClick={() => navigate(`/organization/${organizationId}`)}>
                        {breadcrumbCrumbs.orgLabel}
                      </BreadcrumbLink>
                    </BreadcrumbItem>
                    {detailParent && entityLabel ? (
                      <>
                        <BreadcrumbSeparator />
                        <BreadcrumbItem>
                          <BreadcrumbLink className="cursor-pointer" onClick={() => navigate(`/organization/${organizationId}/${detailParent.path}`)}>
                            {detailParent.label}
                          </BreadcrumbLink>
                        </BreadcrumbItem>
                        <BreadcrumbSeparator />
                        <BreadcrumbItem>
                          <BreadcrumbPage>{entityLabel}</BreadcrumbPage>
                        </BreadcrumbItem>
                      </>
                    ) : breadcrumbCrumbs.pageName ? (
                      <>
                        <BreadcrumbSeparator />
                        <BreadcrumbItem>
                          <BreadcrumbPage>{breadcrumbCrumbs.pageName}</BreadcrumbPage>
                        </BreadcrumbItem>
                      </>
                    ) : null}
                  </>
                )}
              </BreadcrumbList>
            </Breadcrumb>
          )}
          <Button
            variant="ghost"
            size="sm"
            className="ml-auto h-7 gap-1.5 text-muted-foreground hover:text-foreground"
            onClick={() => navigate(`${docsBasePath}/changelog`)}
          >
            <span className="relative">
              <Sparkles className="size-4" />
              {unseenReleases > 0 && <span className="absolute -right-1 -top-1 size-2 rounded-full bg-destructive" />}
            </span>
            What's new
            {unseenReleases > 0 && <span className="sr-only">({unseenReleases} unread)</span>}
          </Button>
        </header>
        <div className="flex-1 min-h-0 flex flex-col overflow-hidden px-6 md:px-12 pt-6 pb-8 animate-in-page">
          <DetailBreadcrumbContext.Provider value={{ entityLabel, setEntityLabel: setEntityLabelStable }}>
            <ErrorBoundary>
              {contentComponent}
            </ErrorBoundary>
          </DetailBreadcrumbContext.Provider>
        </div>
      </SidebarInset>
      <WhatsNewDialog
        unseenReleases={unseenReleases}
        latestRelease={docsManifest?.latestRelease}
        docsBasePath={docsBasePath}
      />
    </SidebarProvider>
  );
};

export default DashboardPage;